import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'

import { DashboardPage } from '@/pages/dashboard'
import { LoginPage } from '@/pages/login'
import { MissionDetailPage } from '@/pages/mission-detail'
import { MissionFormPage } from '@/pages/mission-form'
import { MissionHistoryPage } from '@/pages/mission-history'
import { NotFoundPage } from '@/pages/not-found'
import { useAuthStore } from '@/stores/auth.store'

/**
 * Code-based TanStack Router tree.
 *
 * Three layers:
 *   1. **`rootRoute`** — renders an `<Outlet />` and lets the
 *      router pick which child to mount. We don't paint any chrome
 *      here; that's the job of `AppShell` inside the protected
 *      layout, because the login + 404 pages don't want it.
 *   2. **`loginRoute`** + **`indexRoute`** — public; the index
 *      route is just a redirect to `/dashboard` (or wherever the
 *      auth guard sends us).
 *   3. **`protectedRoute`** — pathless layout route that runs the
 *      auth guard in `beforeLoad`. Every authenticated page hangs
 *      off this route, so the guard runs exactly once per
 *      navigation.
 *
 * The guard reads `useAuthStore.getState()` directly (NOT the
 * `useAuth` hook) — `beforeLoad` runs outside React and we want
 * the freshest state, including the value Zustand may have set
 * synchronously during a refresh.
 *
 * `app.tsx` blocks the router from mounting until `hydrate()`
 * resolves, so by the time `beforeLoad` runs, `hydrated === true`
 * is guaranteed.
 */

/* -------------------------------------------------------------------------- */
/*  Root + public routes                                                       */
/* -------------------------------------------------------------------------- */

const rootRoute = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
})

function RootComponent() {
  return <Outlet />
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // Send the user to the dashboard; the protected guard handles
    // the "not logged in" case from there.
    throw redirect({ to: '/dashboard' })
  },
})

interface LoginSearch {
  redirect?: string
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch =>
    typeof search.redirect === 'string'
      ? { redirect: search.redirect }
      : {},
  component: LoginPage,
})

/* -------------------------------------------------------------------------- */
/*  Protected layout + children                                                */
/* -------------------------------------------------------------------------- */

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  // Pathless route — purely a layout / guard wrapper. The `id`
  // makes it addressable for the router; the path stays empty.
  id: 'authed',
  beforeLoad: ({ location }) => {
    const { user, accessToken } = useAuthStore.getState()
    if (user === null || accessToken === null) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      })
    }
  },
  component: ProtectedLayout,
})

function ProtectedLayout() {
  // The `AppShell` lives inside each page so the layout can
  // remain page-specific where needed (e.g. login / not-found
  // skip the shell). The layout route just renders an outlet.
  return <Outlet />
}

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/dashboard',
  component: DashboardPage,
})

interface MissionFormSearch {
  planningId?: string
}

const missionFormRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/missions/new',
  // `planningId` is optional — present when the user lands here
  // via "Remplir le bon", absent for a free mission. `exactOptional
  // PropertyTypes` rejects `{ planningId: undefined }`, so we omit
  // the key entirely instead of setting it to `undefined`.
  validateSearch: (search: Record<string, unknown>): MissionFormSearch =>
    typeof search.planningId === 'string'
      ? { planningId: search.planningId }
      : {},
  component: MissionFormPage,
})

const missionHistoryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/missions/history',
  component: MissionHistoryPage,
})

const missionDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  // `$id` is TanStack Router's path-param syntax.
  path: '/missions/$id',
  component: MissionDetailPage,
})

/* -------------------------------------------------------------------------- */
/*  Tree + router                                                              */
/* -------------------------------------------------------------------------- */

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    missionFormRoute,
    missionHistoryRoute,
    // `/missions/$id` MUST be declared after the other `/missions/*`
    // routes — TanStack matches in order, and a leading param
    // route would otherwise swallow `/missions/new` and
    // `/missions/history`.
    missionDetailRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
